/**
 * useSamePageRefresh — global click delegate for SPA "refresh-on-same-route".
 *
 * Problem: React Router's <Link> is a no-op when you click a link whose
 * destination is the page you're already viewing. Users (correctly)
 * expect that clicking "Testimonials" while on `/ja/testimonials`
 * should recycle the page: scroll back to the top, reset paginated /
 * filtered state, and feel like a fresh visit — same as if they had
 * clicked the link from a different route.
 *
 * Solution: install a single capture-phase click listener on the
 * document. When the user clicks an internal `<a>` whose pathname
 * matches the current location, we:
 *
 *   1. preventDefault() so React Router doesn't see a same-route
 *      navigation (which would silently do nothing).
 *   2. Smooth-scroll the window back to the top.
 *   3. Dispatch a `jtr:same-page-refresh` CustomEvent on window so
 *      any page can subscribe and reset its own internal state
 *      (e.g. testimonials goes back to page 1).
 *
 * The delegate ignores:
 *   - Modifier-key clicks (cmd/ctrl/shift/alt) so cmd+click still
 *     opens in a new tab.
 *   - Middle-click / right-click so users can still open in a new tab.
 *   - Anchors with `target` set to anything other than `_self`.
 *   - Anchors with explicit `data-no-refresh` opt-out attribute.
 *   - External links (different origin) — those are real navigations.
 *   - Hash-only links (e.g. `#section`) — those scroll within the page.
 *
 * Mount this hook ONCE — Header.jsx (mounted on every page) is the
 * natural home. Multiple mounts are safe but wasteful.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Strip a leading language segment (`/ja` or `/en`) so that
// `/testimonials` and `/ja/testimonials` compare as equivalent — the
// site routes both to the same React component. Without this, header
// links that use the lang-agnostic `/testimonials` href would never
// match the current `/ja/testimonials` location and the delegate
// would let them fall through to React-Router's same-route no-op.
const LANG_PREFIX = /^\/(ja|en)(?=\/|$)/;
const normalizePath = (p) =>
  ((p || '').split('#')[0].split('?')[0].replace(/\/+$/, '') || '/')
    .replace(LANG_PREFIX, '') || '/';

export const SAME_PAGE_REFRESH_EVENT = 'jtr:same-page-refresh';

export function useSamePageRefresh() {
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      // Bail on modified clicks so cmd+click / shift+click still
      // open in new tabs.
      if (e.defaultPrevented) return;
      if (e.button !== undefined && e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // Walk up to find the nearest <a> the user actually clicked.
      const anchor = e.target && e.target.closest && e.target.closest('a');
      if (!anchor) return;

      // Explicit opt-out for callers that need the default no-op.
      if (anchor.dataset && anchor.dataset.noRefresh === 'true') return;

      // Skip new-tab anchors, downloads, mailto/tel.
      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
      // Pure hash links — let the browser do its in-page scroll.
      if (href.startsWith('#')) return;

      // Cross-origin links — let the browser handle them.
      let url;
      try { url = new URL(anchor.href, window.location.origin); }
      catch { return; }
      if (url.origin !== window.location.origin) return;

      // The actual same-route check.
      if (normalizePath(url.pathname) !== normalizePath(location.pathname)) {
        return;
      }

      // We have a same-route click. Recycle the page.
      e.preventDefault();
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      // Dispatch on next microtask so the page's listener doesn't fire
      // in the middle of React's event-handler call stack.
      Promise.resolve().then(() => {
        window.dispatchEvent(new CustomEvent(SAME_PAGE_REFRESH_EVENT, {
          detail: { pathname: location.pathname },
        }));
      });
    };

    // Capture-phase so we run before React-Router's bubble-phase
    // click handler attached to <Link>. Without capture, RR's
    // preventDefault could win and our scrollTo would never fire on
    // some link styles.
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [location.pathname]);
}
